import { flash, SubmitButton, Switch } from "@xola/ui-kit";
import { FormField, requiredNumberMoreThanZero, SwitchField } from "common";
import React from "react";
import { useMutation } from "react-query";
import { object } from "yup";
import { api } from "../../../api/api";
import { ScrollToFieldError } from "../../../helpers/validationHelper";
import { useSeller } from "../../../hooks/useAccount";
import { useInvalidate } from "../../../hooks/useInvalidation";
import { useVisibility } from "../../../hooks/useVisibility";
import { NumberInlineValuePopover } from "../../Common/NumberInlineValuePopover";
import { SecondaryButton } from "../../Common/SecondaryButton";
import { XolaFormik } from "../../Common/XolaFormik";
import { ContentLayout } from "../../Layout/ContentLayout";

const MAX_REUSE_DAYS = 365;

const validationSchema = object().shape({
    days: requiredNumberMoreThanZero().integer().max(MAX_REUSE_DAYS, `must be ${MAX_REUSE_DAYS} days or less`),
});

interface InitialValuesProps {
    multiLinkDayInterval: string | null;
    days: number;
}

export const DigitalWaiversSettings = () => {
    const seller = useSeller();
    const invalidate = useInvalidate();
    const [isDaysOpen, { toggle: toggleDays }] = useVisibility(false);

    const { mutateAsync: updateWaiverPreferences, isLoading: isLoadingWaiverPreferences } = useMutation(
        async (value: string | null) => {
            try {
                await api.post(`/users/${seller.id}/preferences/waiver`, { multiLinkDayInterval: value });
                invalidate("sellers", seller.id);
                flash.success("Successfully updated digital waiver settings");
            } catch {
                flash.danger("Error on updating digital waiver settings");
            }
        },
    );

    const getInitalDays = (): number => {
        const defaultDays = 15;
        const waiverPreference = seller.preferences?.waiver?.multiLinkDayInterval;
        if (waiverPreference === null) {
            return defaultDays;
        }

        if (!waiverPreference) {
            return defaultDays;
        }

        if (waiverPreference?.length) {
            return Number(waiverPreference.slice(1, -1));
        }

        return defaultDays;
    };

    const handleSubmit = async (values: InitialValuesProps) => {
        if (!values.multiLinkDayInterval) {
            return updateWaiverPreferences(null);
        }

        return updateWaiverPreferences(`P${values.days}D`);
    };

    const initialValues: InitialValuesProps = {
        multiLinkDayInterval: seller.preferences?.waiver?.multiLinkDayInterval ?? null,
        days: getInitalDays(),
    };

    return (
        <XolaFormik validationSchema={validationSchema} initialValues={initialValues} onSubmit={handleSubmit}>
            {({ handleSubmit, setFieldValue, values, errors, handleReset }) => {
                const isEnabledWaiverPreferences = values.multiLinkDayInterval;

                return (
                    <ContentLayout className="w-full max-w-full">
                        <div>
                            <h2>Settings</h2>
                            <FormField name="multiLinkDayInterval">
                                <SwitchField
                                    isChecked={!!isEnabledWaiverPreferences}
                                    onChange={(value: boolean) => {
                                        if (value) {
                                            setFieldValue("multiLinkDayInterval", `P${Number(values.days)}D`);
                                        } else {
                                            setFieldValue("multiLinkDayInterval", null);
                                        }
                                    }}
                                >
                                    <Switch.Label direction="left" className="font-bold" size="medium">
                                        Reuse Past Waivers
                                    </Switch.Label>
                                </SwitchField>
                                <div>
                                    Allow previously linked waivers to be linked to a new reservation
                                    {!!isEnabledWaiverPreferences && (
                                        <>
                                            {" "}
                                            if the waiver is less than
                                            <NumberInlineValuePopover
                                                autoSelectOnClick
                                                isOpen={isDaysOpen}
                                                value={values.days}
                                                text={<var data-var="days">{values.days}</var>}
                                                error={errors.days}
                                                min="1"
                                                step="1"
                                                onOpenChange={toggleDays}
                                                onChange={(value) => setFieldValue("days", value)}
                                            />
                                            days old
                                        </>
                                    )}
                                </div>
                            </FormField>
                        </div>
                        <ContentLayout.Footer>
                            <SubmitButton isLoading={isLoadingWaiverPreferences} onClick={handleSubmit}>
                                Save
                            </SubmitButton>

                            <SecondaryButton onClick={handleReset}>Cancel</SecondaryButton>
                        </ContentLayout.Footer>

                        <ScrollToFieldError />
                    </ContentLayout>
                );
            }}
        </XolaFormik>
    );
};
